import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, SectionCard,
  Topbar, LoadingScreen, EmptyState, StatusBadge, ProgressBar, UserProfileCard, AnimatedStatCard
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

function AuthorityDashboardInner() {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [adminPerf, setAdminPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    const [statsRes, assignedRes, perfRes] = await Promise.allSettled([
      api.get('/authority/dashboard-stats'),
      api.get('/authority/assigned-complaints'),
      api.get('/dashboard/admin/performance'),
    ]);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    if (assignedRes.status === 'fulfilled') setAssigned(Array.isArray(assignedRes.value.data) ? assignedRes.value.data : []);
    if (perfRes.status === 'fulfilled') setAdminPerf(Array.isArray(perfRes.value.data) ? perfRes.value.data : []);
    setLoading(false);
    setError(statsRes.status === 'rejected' ? (statsRes.reason?.response?.data?.error || 'Failed to load') : '');
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  if (loading) return <LoadingScreen />;

  const isDept = stats?.authority_type === 'department';
  const deptLabel = isDept ? (stats?.department || 'Department') : null;

  const complaintCards = [
    { label: 'Total',        value: stats?.totalComplaints     || 0, color: COLORS.purple },
    { label: 'Pending',      value: stats?.pendingComplaints   || 0, color: COLORS.amber },
    { label: 'Verified',     value: stats?.verifiedComplaints  || 0, color: COLORS.blue },
    { label: 'Resolved',     value: stats?.resolvedComplaints  || 0, color: COLORS.green },
    { label: 'Escalated',    value: stats?.escalatedComplaints || 0, color: COLORS.red },
    { label: 'Assigned to Me', value: stats?.assignedToMe      || 0, color: COLORS.cyan, onClick: () => navigate('/authority/assigned') },
  ];

  const userCards = [
    { label: 'Total Users',  value: stats?.totalUsers       || 0, color: COLORS.blue },
    { label: 'Students',     value: stats?.totalStudents    || 0, color: COLORS.cyan },
    { label: 'Faculty',      value: stats?.totalFaculty     || 0, color: COLORS.violet },
    { label: 'Admins',       value: stats?.totalAdmins      || 0, color: COLORS.amber },
    { label: 'Authorities',  value: stats?.totalAuthorities || 0, color: COLORS.teal },
  ];

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)}
          mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title={isDept ? `${deptLabel} Authority Dashboard` : 'University Authority Dashboard'}
            subtitle={isDept ? `${deptLabel} department oversight` : 'University-wide oversight and management'}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={fetchAll}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            <UserProfileCard user={user} />

            {/* Complaints section */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                {isDept ? `\uD83D\uDCCB ${deptLabel} Complaints` : '\uD83D\uDCCB University-Level Complaints'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
                {complaintCards.map(({ label, value, color, icon, onClick }, i) => (
                  <AnimatedStatCard key={label} label={label} value={value} color={color} onClick={onClick} delay={`${0.05 + i * 0.06}s`} />
                ))}
              </div>

              {/* Users section */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                {isDept ? `\uD83D\uDC65 ${deptLabel} Users` : '\uD83D\uDC65 University Users'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                {userCards.map(({ label, value, color, icon }, i) => (
                  <AnimatedStatCard key={label} label={label} value={value} color={color} compact delay={`${0.05 + i * 0.06}s`} />
                ))}
              </div>
            </div>

            {/* Assigned complaints table */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16, marginBottom: 20 }}>
              <SectionCard title="Assigned Complaints" subtitle="Escalated to you" delay="0.45s"
                action={
                  <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/authority/assigned')}>View All</button>
                }>
                {!assigned.length
                  ? <EmptyState text="No assigned complaints" sub="Nothing escalated yet" />
                  : (
                    <table style={s.table}>
                      <thead style={s.thead}>
                        <tr>
                          <th style={s.th}>Complaint</th>
                          <th style={s.th}>Status</th>
                          <th style={s.th}>Dept</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assigned.slice(0, 6).map(c => (
                          <tr key={c.id} className="ur-row-hover" onClick={() => navigate(`/authority/resolve/${c.id}`)}>
                            <td style={s.tdBold}>
                              {c.title}
                              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{c.student_name}</div>
                            </td>
                            <td style={s.td}><StatusBadge status={c.status} /></td>
                            <td style={s.td}><span style={{ fontSize: 11 }}>{c.department || '\u2014'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </SectionCard>

              <SectionCard title="Admin Performance & Analytics" subtitle="Verification, resolution and negligence tracking" delay="0.5s">
                {!adminPerf.length
                  ? <EmptyState text="No admin data" sub="No admins assigned yet" />
                  : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                      {adminPerf.map(a => {
                        const maxV = Math.max(...adminPerf.map(x => x.verified_count || 0), 1);
                        const maxR = Math.max(...adminPerf.map(x => x.resolved_count || 0), 1);
                        const neglPct = a.negligence_pct || 0;
                        const neglColor = neglPct >= 60 ? COLORS.red : neglPct >= 30 ? COLORS.amber : COLORS.green;
                        return (
                          <div key={a.id} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: '#fff' }}>
                                  {a.name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{a.name}</div>
                                  <div style={{ fontSize: 11, color: theme.textMuted }}>{a.department || 'University'} • {a.admin_type === 'university' ? 'Uni Admin' : 'Dept Admin'}</div>
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 11, color: neglColor, fontWeight: 700 }}>{neglPct}% negligence</div>
                                <div style={{ fontSize: 10, color: theme.textMuted }}>{a.negligent_count || 0} overdue</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                              {[
                                { label: 'Assigned', val: a.assigned_count || 0, color: COLORS.cyan },
                                { label: 'Verified', val: a.verified_count || 0, color: COLORS.blue },
                                { label: 'Resolved', val: a.resolved_count || 0, color: COLORS.green },
                                { label: 'Pending', val: a.pending_count || 0, color: COLORS.amber },
                              ].map(({ label, val, color }) => (
                                <div key={label} style={{ background: color + '18', border: '1px solid ' + color + '33', borderRadius: 8, padding: '4px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 56 }}>
                                  <span style={{ fontSize: 15, fontWeight: 800, color }}>{val}</span>
                                  <span style={{ fontSize: 10, color: theme.textMuted }}>{label}</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 10, color: theme.textMuted }}>Verification Rate</span>
                                <span style={{ fontSize: 10, color: COLORS.blue, fontWeight: 600 }}>{Math.round(((a.verified_count||0)/maxV)*100)}%</span>
                              </div>
                              <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: Math.round(((a.verified_count||0)/maxV)*100) + '%', background: 'linear-gradient(90deg,' + COLORS.blue + ',' + COLORS.cyan + ')', borderRadius: 4, transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 10, color: theme.textMuted }}>Resolution Rate</span>
                                <span style={{ fontSize: 10, color: COLORS.green, fontWeight: 600 }}>{Math.round(((a.resolved_count||0)/maxR)*100)}%</span>
                              </div>
                              <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: Math.round(((a.resolved_count||0)/maxR)*100) + '%', background: 'linear-gradient(90deg,' + COLORS.green + ',' + COLORS.teal + ')', borderRadius: 4, transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ fontSize: 10, color: theme.textMuted }}>Negligence (overdue)</span>
                                <span style={{ fontSize: 10, color: neglColor, fontWeight: 700 }}>{neglPct}%</span>
                              </div>
                              <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: neglPct + '%', background: 'linear-gradient(90deg,' + neglColor + ',' + neglColor + '99)', borderRadius: 4, transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </SectionCard>
            </div>

            <SectionCard title="Quick Actions" delay="0.55s">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/authority/assigned')}>Assigned Complaints</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/authority/reports')}>Reports</button>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthorityDashboard() {
  return (
    <ThemeProvider>
      <AuthorityDashboardInner />
    </ThemeProvider>
  );
}
